import { TAU, wrapAngle } from '@gardener/core';
import { type ByteReader, type ByteWriter, ProtocolError } from './bytes';

/**
 * A tiny binary schema DSL. Wire shapes are declared once as codecs; TypeScript types are inferred from
 * them (`Infer<typeof X>`). Quantizing codecs (fixed, angle16) are lossy by design — use
 * `quantizeAngle` & co. when the sender must simulate with the exact value the receiver will see.
 */
export interface Codec<T> {
  write(w: ByteWriter, v: T): void;
  read(r: ByteReader): T;
  /**
   * Do two values look alike on the wire (ADR-0204: a snapshot's delta leaves out a field that didn't change)? A
   * quantizing codec compares what it would write; without one, `===`.
   */
  same?(a: T, b: T): boolean;
  /**
   * A delta writes it as a step from the value it had (ADR-0204), in the codec's own units as a zig-zag varint: `of` the
   * step from `base`, `add` it back on to it. Exact: the base a client holds is already the codec's (quantized).
   */
  step?: { of(v: T, base: T): number; add(base: T, step: number): T };
}

export type Infer<C> = C extends Codec<infer T> ? T : never;

const codec = <T>(
  write: Codec<T>['write'],
  read: Codec<T>['read'],
  extra?: Pick<Codec<T>, 'same' | 'step'>,
): Codec<T> => ({
  write,
  read,
  ...extra,
});

/** Do `a` and `b` look alike on the wire as `c` writes them (its `same`, else `===`)? */
export const alike = <T>(c: Codec<T>, a: T, b: T): boolean => (c.same ? c.same(a, b) : a === b);

const ANGLE_STEPS = 65536;
const encodeAngle16 = (a: number) => Math.round((wrapAngle(a) / TAU) * ANGLE_STEPS) & 0xffff;
const decodeAngle16 = (v: number) => wrapAngle((v / ANGLE_STEPS) * TAU);

/** What an angle looks like after an angle16 round trip. Idempotent. */
export const quantizeAngle = (a: number): number => decodeAngle16(encodeAngle16(a));

/** What a `fixed(scale)` value looks like after a round trip. */
export const quantizeFixed = (v: number, scale: number): number => Math.round(v * scale) / scale;

export const t = {
  u8: codec<number>(
    (w, v) => w.u8(v),
    (r) => r.u8(),
  ),
  i8: codec<number>(
    (w, v) => w.i8(v),
    (r) => r.i8(),
  ),
  u16: codec<number>(
    (w, v) => w.u16(v),
    (r) => r.u16(),
  ),
  varuint: codec<number>(
    (w, v) => w.varuint(v),
    (r) => r.varuint(),
  ),
  varint: codec<number>(
    (w, v) => w.varint(v),
    (r) => r.varint(),
  ),
  f32: codec<number>(
    (w, v) => w.f32(v),
    (r) => r.f32(),
  ),
  f64: codec<number>(
    (w, v) => w.f64(v),
    (r) => r.f64(),
  ),
  bool: codec<boolean>(
    (w, v) => w.u8(v ? 1 : 0),
    (r) => r.u8() !== 0,
  ),
  string: codec<string>(
    (w, v) => w.string(v),
    (r) => r.string(),
  ),
  /** Signed fixed point: `round(v * scale)` as a zig-zag varint. 1/16 px positions → `fixed(16)`. */
  fixed: (scale: number) =>
    codec<number>(
      (w, v) => w.varint(Math.round(v * scale)),
      (r) => r.varint() / scale,
      {
        same: (a, b) => Math.round(a * scale) === Math.round(b * scale),
        step: {
          of: (v, b) => Math.round(v * scale) - Math.round(b * scale),
          add: (b, d) => (Math.round(b * scale) + d) / scale,
        },
      },
    ),
  /** A unit vector's component (-1..1) in one byte, in 1/127 steps. */
  unit8: codec<number>(
    (w, v) => w.i8(Math.round(Math.max(-1, Math.min(1, v)) * 127)),
    (r) => r.i8() / 127,
  ),
  /** Angle in radians as 16 bits (~0.0055° resolution). */
  angle16: codec<number>(
    (w, v) => w.u16(encodeAngle16(v)),
    (r) => decodeAngle16(r.u16()),
    {
      same: (a, b) => encodeAngle16(a) === encodeAngle16(b),
      // The short way round: -32768 … 32767 steps.
      step: {
        of: (v, b) => ((encodeAngle16(v) - encodeAngle16(b) + 3 * 32768) % ANGLE_STEPS) - 32768,
        add: (b, d) => decodeAngle16((encodeAngle16(b) + d) & 0xffff),
      },
    },
  ),
  /** String enum stored as its index in `values`. */
  enumOf: <const V extends readonly string[]>(values: V) =>
    codec<V[number]>(
      (w, v) => {
        const i = values.indexOf(v);
        if (i < 0) throw new ProtocolError(`Unknown enum value ${v}`);
        w.u8(i);
      },
      (r) => {
        const i = r.u8();
        const v = values[i];
        if (v === undefined) throw new ProtocolError(`Enum index ${i} out of range`);
        return v;
      },
    ),
};

export function array<T>(item: Codec<T>): Codec<T[]> {
  return codec(
    (w, list) => {
      w.varuint(list.length);
      for (const v of list) item.write(w, v);
    },
    (r) => {
      const n = r.varuint();
      if (n > 65535) throw new ProtocolError(`Array too long: ${n}`);
      const out: T[] = new Array(n);
      for (let i = 0; i < n; i++) out[i] = item.read(r);
      return out;
    },
  );
}

export function optional<T>(inner: Codec<T>): Codec<T | null> {
  return codec(
    (w, v) => {
      w.u8(v === null ? 0 : 1);
      if (v !== null) inner.write(w, v);
    },
    (r) => (r.u8() === 0 ? null : inner.read(r)),
    { same: (a, b) => (a === null || b === null ? a === b : alike(inner, a, b)) },
  );
}

type Shape = Record<string, Codec<unknown>>;
type StructOf<S extends Shape> = { [K in keyof S]: Infer<S[K]> };

export interface StructCodec<S extends Shape> extends Codec<StructOf<S>> {
  shape: S;
  same(a: StructOf<S>, b: StructOf<S>): boolean;
  /**
   * What changed in `v` since `base` on the wire (ADR-0204), its first `from` keys aside: the fields unlike `base`'s, a
   * stepping one (`Codec.step`) as its step from it; null when none differ.
   */
  change(v: StructOf<S>, base: StructOf<S>, from?: number): Partial<StructOf<S>> | null;
  /** `base` with `change` laid on it: the struct `change` was taken from, as the wire has it. */
  apply(base: StructOf<S>, change: Partial<StructOf<S>>): StructOf<S>;
  /** Writes a `change`: a bitmask of the keys it holds (bit i: key `from + i` in declaration order), then each in that order. */
  writeChange(w: ByteWriter, change: Partial<StructOf<S>>, from?: number): void;
  /** Reads what `writeChange` wrote. */
  readChange(r: ByteReader, from?: number): Partial<StructOf<S>>;
}

/** Fields are encoded in declaration order. */
export function struct<S extends Shape>(shape: S): StructCodec<S> {
  const keys = Object.keys(shape) as (keyof S & string)[];
  const field = (k: keyof S & string) => shape[k] as Codec<unknown>;
  const maskable = (from: number) => {
    if (keys.length - from > 32) throw new ProtocolError(`${keys.length - from} fields for a 32-bit mask`);
  };
  type V = StructOf<S>;
  type K = keyof S & string;
  return {
    shape,
    write(w, v) {
      for (const k of keys) field(k).write(w, v[k]);
    },
    read(r) {
      const out = {} as V;
      for (const k of keys) out[k] = field(k).read(r) as V[K];
      return out;
    },
    same(a, b) {
      for (const k of keys) if (!alike(field(k), a[k], b[k])) return false;
      return true;
    },
    change(v, base, from = 0) {
      const out: Partial<V> = {};
      let any = false;
      for (let i = from; i < keys.length; i++) {
        const k = keys[i] as K;
        const c = field(k);
        if (alike(c, v[k], base[k])) continue;
        out[k] = (c.step ? c.step.of(v[k], base[k]) : v[k]) as V[K];
        any = true;
      }
      return any ? out : null;
    },
    apply(base, change) {
      const out = { ...base };
      for (const k of Object.keys(change) as K[]) {
        const c = field(k);
        out[k] = (c.step ? c.step.add(base[k], change[k] as number) : change[k]) as V[K];
      }
      return out;
    },
    writeChange(w, change, from = 0) {
      maskable(from);
      let mask = 0;
      for (let i = from; i < keys.length; i++)
        if ((keys[i] as string) in change) mask = (mask | (1 << (i - from))) >>> 0;
      w.varuint(mask);
      for (let i = from; i < keys.length; i++) {
        const k = keys[i] as K;
        if (!(k in change)) continue;
        const c = field(k);
        if (c.step) w.varint(change[k] as number);
        else c.write(w, change[k]);
      }
    },
    readChange(r, from = 0) {
      maskable(from);
      const mask = r.varuint();
      if (keys.length - from < 32 && mask >>> (keys.length - from) !== 0)
        throw new ProtocolError(`Mask ${mask} too wide`);
      const out: Partial<V> = {};
      for (let i = from; i < keys.length; i++) {
        const k = keys[i] as K;
        if (!((mask >>> (i - from)) & 1)) continue;
        const c = field(k);
        out[k] = (c.step ? r.varint() : c.read(r)) as V[K];
      }
      return out;
    },
  };
}

type VariantsOf<V extends Record<string, Codec<object>>, Tag extends string> = {
  [K in keyof V & string]: { [P in Tag]: K } & Infer<V[K]>;
}[keyof V & string];

/**
 * Discriminated union keyed by `tag`, encoded as a u8 variant index (declaration order) followed by the
 * variant's struct. Any change is a protocol change, appending a variant too: a reader throws on an index it doesn't
 * know, and structs carry no length, so an old peer can't skip what it doesn't understand. Only `hello` and `reject`
 * are readable across versions (ADR-0062).
 */
export function taggedUnion<const Tag extends string, V extends Record<string, Codec<object>>>(
  tag: Tag,
  variants: V,
): Codec<VariantsOf<V, Tag>> & { readonly names: readonly (keyof V & string)[]; readonly variants: V } {
  const names = Object.keys(variants) as (keyof V & string)[];
  if (names.length > 255) throw new Error('Too many variants');
  return {
    /** The variants by their index on the wire. */
    names,
    variants,
    write(w, v) {
      const name = (v as Record<string, unknown>)[tag] as string;
      const i = names.indexOf(name);
      if (i < 0) throw new ProtocolError(`Unknown ${tag} '${name}'`);
      w.u8(i);
      (variants[name] as Codec<object>).write(w, v as object);
    },
    read(r) {
      const i = r.u8();
      const name = names[i];
      if (name === undefined) throw new ProtocolError(`Unknown ${tag} index ${i}`);
      const body = (variants[name] as Codec<object>).read(r);
      return { [tag]: name, ...body } as VariantsOf<V, Tag>;
    },
  };
}
